import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronDown, ChevronRight } from 'lucide-react';
import styles from './Sidebar.module.css';
import { useToast } from '../../contexts/ToastContext';

const Sidebar = ({
  onUploadFile,
  onLoadData,
  onRefreshVisuals,
  refreshStatus,
  serverStatus,
  getRefreshButtonText,
  isUploading,
  selectedTier,
  onTierChange,
  hasSelectedFile = false,
  nestedNav = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { showError } = useToast();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return true;
    }
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  // Every expandable group starts open, so nothing is hidden behind a
  // chevron on arrival. Collapsing one is still a click, and that choice
  // sticks for the session.
  const [expandedItems, setExpandedItems] = useState(() => ['erp', 'analytics', 'admin']);

  const [expandedSections, setExpandedSections] = useState(() => new Set());

  const isAdmin = user?.role === 'admin';

  const getUserTier = () => {
    if (!user) return 'B';
    const tier = user?.productTier || user?.product_tier || 'B';
    let normalizedTier = 'B';
    if (Array.isArray(tier)) {
      normalizedTier = String(tier[0] || 'B').trim();
    } else {
      normalizedTier = String(tier || 'B').trim();
    }
    if (!['B', 'A', 'A+'].includes(normalizedTier)) {
      normalizedTier = 'B';
    }
    return normalizedTier;
  };

  const isTierAccessible = (tier) => {
    if (!user) return false;

    const userTier = getUserTier();
    const tierMap = {
      Beta: 'B',
      Alpha: 'A',
      'Alpha+': 'A+',
    };

    const requiredTier = tierMap[tier];
    if (!requiredTier) return false;

    if (userTier === 'B') return requiredTier === 'B';
    if (userTier === 'A') return requiredTier === 'B' || requiredTier === 'A';
    if (userTier === 'A+') return true;
    return false;
  };

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebar-collapsed', JSON.stringify(newState));
  };

  const toggleExpand = (itemId) => {
    setExpandedItems((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const handleNavigate = (path, options = {}) => {
    navigate(path, options);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleExploreTier = (tier) => {
    if (!isTierAccessible(tier)) {
      showError('You do not have access to this tier.');
      return;
    }

    if (onTierChange) {
      onTierChange(tier);
    }
    localStorage.setItem('dashboard-selected-tier', tier);
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard', { state: { tier } });
    }
  };

  const primaryItems = [
    {
      id: 'home',
      label: 'Home',
      action: () => handleNavigate('/'),
      navigable: true,
    },
    {
      id: 'erp',
      label: 'ERP',
      action: () => handleNavigate('/erp'),
      navigable: true,
    },
    {
      // Sits beside Analytics rather than under it: while the Power BI
      // dashboard is open, Analytics renders the report tree through
      // nestedNav, which replaces any children it would otherwise have.
      id: 'native-visuals',
      label: 'Dashboards',
      action: () => handleNavigate('/analytics/native-visuals'),
      navigable: true,
    },
    {
      id: 'analytics',
      label: 'Analytics',
      action: () => {
        const defaultTier =
          selectedTier || localStorage.getItem('dashboard-selected-tier') || 'Beta';
        handleExploreTier(defaultTier);
      },
      navigable: true,
    },
  ];

  if (isAdmin) {
    primaryItems.push({
      id: 'admin',
      label: 'Admin',
      action: () => handleNavigate('/admin-dashboard'),
      navigable: true,
    });
  }

  primaryItems.push({
    id: 'refresh-visuals',
    label: getRefreshButtonText ? getRefreshButtonText() : 'Refresh visuals',
    action: onRefreshVisuals || (() => {}),
    navigable: true,
    disabled: true,
  });

  const secondaryItems = [
    {
      id: 'profile',
      label: 'User profile',
      action: () => handleNavigate('/profile'),
      navigable: true,
    },
    {
      id: 'activity',
      label: 'Activity',
      action: () => handleNavigate('/activity'),
      navigable: true,
    },
  ];

  const bottomItems = [
    {
      id: 'report-issue',
      label: 'Report issue',
      action: () => handleNavigate('/report-issue'),
      navigable: true,
    },
    {
      id: 'Logout',
      label: 'Logout',
      action: () => handleLogout(),
      navigable: true,
    },
  ];

  const isActive = (item) => {
    if (item.id === 'home' && (location.pathname === '/' || location.pathname === '')) return true;
    if (item.id === 'profile' && location.pathname === '/profile') return true;
    if (item.id === 'erp' && (location.pathname === '/erp' || location.pathname === '/templates'))
      return true;
    if (item.id === 'analytics' && location.pathname === '/dashboard') return true;
    if (item.id === 'native-visuals' && location.pathname === '/analytics/native-visuals')
      return true;
    if (item.id === 'activity' && location.pathname === '/activity') return true;
    if (item.id === 'report-issue' && location.pathname === '/report-issue') return true;
    if (item.id === 'admin' && location.pathname.startsWith('/admin')) return true;
    if (item.id === 'create-schema' && location.pathname === '/visuals/create-table') return true;
    if (item.id === 'manage-tables' && location.pathname === '/visuals/tables') return true;
    if (item.id === 'upload-data' && location.pathname === '/visuals/upload') return true;
    if (item.id === 'view-data' && location.pathname === '/visuals/view-data') return true;
    return false;
  };

  const hasNestedNav = (itemId) =>
    Boolean(nestedNav?.parentId === itemId && Array.isArray(nestedNav.sections));

  useEffect(() => {
    if (!nestedNav?.parentId) return;
    setExpandedItems((prev) =>
      prev.includes(nestedNav.parentId) ? prev : [...prev, nestedNav.parentId]
    );
  }, [nestedNav?.parentId, location.pathname]);

  const nestedSectionIdsKey = (nestedNav?.sections || [])
    .map((section, index) => section.id || `section-${index}`)
    .join('|');

  // Expand all nested sidebar sections by default (master data, your pages, etc.)
  useEffect(() => {
    if (!nestedSectionIdsKey) {
      setExpandedSections(new Set());
      return;
    }
    setExpandedSections(new Set(nestedSectionIdsKey.split('|')));
  }, [nestedNav?.parentId, nestedSectionIdsKey]);

  const toggleSection = (sectionId) => {
    if (!sectionId) return;
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const renderNestedSections = (parentId) => {
    if (!hasNestedNav(parentId) || isCollapsed) return null;
    const sections = nestedNav.sections || [];
    const activeId = nestedNav.activeId;

    return (
      <div className={styles.nestedNav}>
        {sections.map((section, index) => {
          const sectionId = section.id || `section-${index}`;
          const isSectionExpanded = expandedSections.has(sectionId);
          const itemCount = (section.items || []).length;

          return (
            <div key={sectionId} className={styles.nestedSection}>
              {index > 0 && <div className={styles.nestedDivider} />}
              {(section.label || section.onAddNew) && (
                <div className={styles.sectionHeader}>
                  <button
                    type="button"
                    className={styles.sectionToggle}
                    onClick={() => toggleSection(sectionId)}
                    aria-expanded={isSectionExpanded}
                    title={isSectionExpanded ? 'Collapse' : 'Expand'}
                  >
                    <span className={styles.sectionChevron}>
                      {isSectionExpanded ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </span>
                    {section.label ? (
                      <span className={styles.sectionLabel}>{section.label}</span>
                    ) : (
                      <span className={styles.sectionLabel}>items</span>
                    )}
                  </button>
                  {section.onAddNew && (
                    <button
                      type="button"
                      className={styles.sectionAddBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        section.onAddNew();
                      }}
                      title={section.addNewLabel || 'Add new'}
                      aria-label={section.addNewLabel || 'Add new'}
                    >
                      +
                    </button>
                  )}
                </div>
              )}
              {isSectionExpanded && (
                <div className={styles.nestedItemList}>
                  {section.loading
                    ? Array.from({ length: 3 }).map((_, skeletonIndex) => (
                        <div key={skeletonIndex} className={styles.nestedSkeleton} />
                      ))
                    : itemCount === 0
                      ? (
                        <div className={styles.nestedEmpty}>No items yet</div>
                      )
                      : (section.items || []).map((item) => {
                          const itemActive = String(item.id) === String(activeId);
                          const itemDisabled = Boolean(item.disabled);
                          return (
                            <button
                              key={item.id}
                              type="button"
                              className={`${styles.nestedItem} ${itemActive ? styles.nestedItemActive : ''} ${
                                itemDisabled ? styles.nestedItemDisabled : ''
                              }`}
                              disabled={itemDisabled}
                              title={item.disabledReason || item.label}
                              onClick={() => {
                                if (itemDisabled) return;
                                if (item.action) item.action();
                                else if (section.onSelect) section.onSelect(item.id);
                                else if (nestedNav.onSelect) nestedNav.onSelect(item.id);
                              }}
                            >
                              <span className={styles.nestedItemLabel}>{item.label}</span>
                            </button>
                          );
                        })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderItem = (item, level = 0) => {
    const nested = hasNestedNav(item.id);
    const hasChildren = (item.children && item.children.length > 0) || nested;
    const isExpanded = expandedItems.includes(item.id);
    const active = isActive(item);
    const isChild = level > 0;
    const tierDisabled = item.tier ? !isTierAccessible(item.tier) : false;
    const isDisabled =
      item.disabled || tierDisabled || (!item.navigable && !hasChildren);
    const initial = (item.label || '?').trim().charAt(0).toUpperCase();

    return (
      <div key={item.id}>
        <div
          className={`${styles.menuItem} ${active ? styles.active : ''} ${
            isDisabled ? styles.disabled : ''
          } ${isChild ? styles.menuItemChild : ''} ${item.id === 'Logout' ? styles.logout : ''}`}
          onClick={() => {
            if (isDisabled) return;
            if (hasChildren) {
              if (item.action && item.navigable && !item.disabled) {
                item.action();
              }
              if (!isExpanded) toggleExpand(item.id);
            } else if (item.action && item.navigable && !item.disabled) {
              item.action();
            }
          }}
          title={item.label}
        >
          <div className={styles.menuItemContent}>
            {isCollapsed ? (
              <span className={styles.menuItemInitial}>{initial}</span>
            ) : (
              <>
                <span
                  className={`${styles.chevronIcon} ${hasChildren ? '' : styles.chevronPlaceholder}`}
                  onClick={(e) => {
                    if (!hasChildren) return;
                    e.stopPropagation();
                    if (!isDisabled) toggleExpand(item.id);
                  }}
                  aria-hidden={!hasChildren}
                >
                  {hasChildren ? (
                    isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                  ) : null}
                </span>
                {item.icon && <span className={styles.menuItemIcon}>{item.icon}</span>}
                <span className={styles.menuItemLabel}>{item.label}</span>
              </>
            )}
          </div>
        </div>
        {hasChildren && isExpanded && !isCollapsed && (
          <div className={styles.childrenContainer}>
            {nested
              ? renderNestedSections(item.id)
              : item.children.map((child) => renderItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    const sidebarWidth = isCollapsed ? 64 : nestedNav ? 288 : 256;
    document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
  }, [isCollapsed, nestedNav]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        const saved = localStorage.getItem('sidebar-collapsed');
        if (!saved || !JSON.parse(saved)) {
          setIsCollapsed(true);
          localStorage.setItem('sidebar-collapsed', JSON.stringify(true));
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div
      className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : styles.expanded} ${
        nestedNav && !isCollapsed ? styles.withNested : ''
      }`}
    >
      <div className={styles.sidebarHeader}>
        {!isCollapsed && <div className={styles.sidebarTitle}>Navigation</div>}
        <button
          onClick={toggleSidebar}
          className={styles.toggleButton}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <div className={styles.hamburger}>
            <span className={styles.hamburgerLine}></span>
            <span className={styles.hamburgerLine}></span>
            <span className={styles.hamburgerLine}></span>
          </div>
        </button>
      </div>

      <div className={styles.sidebarContent}>
        <div className={styles.navGroup}>
          {primaryItems.map((item) => renderItem(item))}
          <div className={styles.navDivider} />
          {secondaryItems.map((item) => renderItem(item))}
        </div>

        <div className={styles.navGroup}>
          {bottomItems.map((item) => renderItem(item))}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
